import getters from '@/study-search/store/getters';
import { MetaTags } from './MetaTags';
import { computed, Ref, ref } from 'vue';
import { PageTitleConstructor } from '@/study-search/models/page-title';
import { PaginationDefinition } from '@/study-search/models/definition';
import { Indexability } from '@/study-search/models/indexability/Indexability';

export class MetaInfo {
	private readonly pageTitleConstructor = new PageTitleConstructor();

	private isIndexable: Ref<boolean> = ref(false);
	private indexability = Indexability.getInstance();

	public async setIndexableFlag(): Promise<void> {
		const shouldIndex = await this.indexability.shouldIndex();

		this.isIndexable.value = shouldIndex;
	}

	public title = computed((): string => {
		return this.pageTitleConstructor.getMetaTitle();
	});

	public robots = computed((): string => {
		return this.isIndexable.value && this.isSelfCanonical.value ? MetaTags.INDEX : MetaTags.NOINDEX;
	});

	public description = computed((): string => {
		return this.pageTitleConstructor.getMetaDescription();
	});

	public canonical = computed((): string => {
		return getters.getCanonical();
	});

	public showPrerenderStatusCode = computed((): boolean => {
		return this.robots.value !== MetaTags.INDEX.toString();
	});

	public showPrerenderHeader = computed((): boolean => {
		return !this.isSelfCanonical.value;
	});

	public showRelPrev = computed((): boolean => {
		const pageNumber: number = getters.getPageNumber();

		return pageNumber !== undefined && pageNumber === 2;
	});

	public relPrev = computed((): string => {
		return `${getters.getCanonical().replace(`/${PaginationDefinition.PAGE_TWO}`, '')}`;
	});

	public showRelNextPage2 = computed((): boolean => {
		const pageNumber: number = getters.getPageNumber();
		const totalSearchResults = getters.getTotalSearchResults();

		if (pageNumber === undefined) {
			return false;
		}

		return pageNumber === 1 && totalSearchResults > Number(PaginationDefinition.PAGE_SIZE);
	});

	public relNextPage2 = computed((): string => {
		return `${getters.getCanonical()}/${PaginationDefinition.PAGE_TWO}`;
	});

	public showRelNextPage3 = computed((): boolean => {
		const pageNumber: number = getters.getPageNumber();
		const totalSearchResults = getters.getTotalSearchResults();

		if (pageNumber === undefined) {
			return false;
		}

		return pageNumber === 2 && totalSearchResults > PaginationDefinition.PAGE_SIZE * 2;
	});

	public relNextPage3 = computed((): string => {
		return `${getters
			.getCanonical()
			.replace(`/${PaginationDefinition.PAGE_TWO}`, `${PaginationDefinition.PAGE_THREE}`)}`;
	});

	/**
	 * Sets the Prerender header in accordance with the canonical URL, since the `prerender-header` meta tag acts as the canonical URL for Prerender.
	 */
	public prerenderHeader = computed((): string => {
		return `Location: ${this.canonical.value}`;
	});

	/**
	 * Sets the Prerender status code in accordance with the indexability policy and canonical URL.
	 */
	public prerenderStatusCode = computed((): number => {
		if (this.showPrerenderHeader.value) {
			return 301;
		}

		return 200;
	});

	/**
	 * Returns true if the canonical URL is the same as the current path (without the `bot` query parameter).
	 */
	private isSelfCanonical = computed((): boolean => {
		const url = new URL(window.location.href);
		url.searchParams.delete('bot');
		return this.canonical.value === url.toString();
	});
}
